import { gql } from "@codesandbox/api";
import { useEnvironmentInterface } from "environment-interface";
import { useBranchEditor } from "features/editor/Feature";
import { toSentryContext } from "features/utils/sentry";
import { useEffect, useMemo } from "react";
import useSWR from "swr";

import { csbApi } from "utils/csbApi";

type RepositoryBranchesArgs = {
  owner: string;
  repo: string;
  workspaceId: string | null;
};

const repositoryBranchesQuery = gql.createQuery(
  "RepositoryBranches",
  ({ owner, repo, workspaceId }: RepositoryBranchesArgs) => ({
    project: [
      {
        owner,
        repo,
        gitProvider: gql.GitProvider.GITHUB,
        ...(workspaceId ? { team: workspaceId } : {}),
      },
      {
        branches: {
          name: true,
          upstream: true,
          lastAccessedAt: true,
        },
      },
    ],
  }),
);

/*
  - Fetches braches from both codesandbox and github
    If any branch is present in both the places, the csb branches are given priority
*/
export const useRepositoryBranches = (shouldFetch = true) => {
  const {
    owner,
    repo,
    defaultBranchName: defaultBranch,
    workspace,
  } = useBranchEditor();
  const { sentry } = useEnvironmentInterface();

  const gqlId = `repo-branches-${owner}/${repo}/${workspace?.id ?? null}`;
  const restPath = `/beta/sandboxes/github/${owner}/${repo}/all_branches`;

  const { data: csbBranches, error: csbError } = useSWR(
    shouldFetch ? gqlId : null,
    () => fetchCsbBranches(owner, repo, workspace?.id ?? null),
  );

  const { data: githubBranches, error: githubError } = useSWR(
    shouldFetch ? restPath : null,
    () => {
      return fetchGitHubBranches(owner, repo);
    },
  );

  const isFetching = csbBranches === undefined || githubBranches === undefined;

  useEffect(() => {
    if (githubError) {
      sentry.reportError(
        githubError.message,
        toSentryContext("branchManagement", {
          state: "FETCHING_REMOTE_BRANCHES",
        }),
      );
    }

    if (csbError) {
      sentry.reportError(
        csbError.message,
        toSentryContext("branchManagement", {
          state: "FETCHING_CODESANDBOX_BRANCHES",
        }),
      );
    }
  }, [csbError, githubError]);

  const csb = useMemo(() => {
    return csbBranches?.project?.branches
      .sort((a, b) => {
        const B = new Date(b?.lastAccessedAt || "").getTime();
        const A = new Date(a?.lastAccessedAt || "").getTime();
        return B - A;
      })
      .reduce((acc: string[], { name }) => {
        if (name !== defaultBranch) {
          acc.push(name);
        }
        return acc;
      }, []);
  }, [defaultBranch, csbBranches]);

  const github = useMemo(() => {
    return githubBranches?.branches.reduce((acc: string[], branch) => {
      if (branch.name !== defaultBranch && !csb?.includes(branch.name)) {
        acc.push(branch.name);
      }
      return acc;
    }, []);
  }, [csb, githubBranches, defaultBranch]);

  return {
    github: github ?? [],
    csb: csb ?? [],
    defaultBranch,
    isFetching,
  };
};

export function fetchCsbBranches(
  owner: string,
  repo: string,
  ownerWorkspaceId: string | null,
) {
  return csbApi.gql.query(repositoryBranchesQuery, {
    owner,
    repo,
    workspaceId: ownerWorkspaceId,
  });
}

export function fetchGitHubBranches(
  owner: string,
  repo: string,
): Promise<{
  branches: Array<{ name: string }>;
  default: { name: string };
}> {
  const restPath = `/beta/sandboxes/github/${owner}/${repo}/all_branches`;

  return csbApi.get({
    path: restPath,
  });
}
